import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const NARRATORS = [
  { id: 'thomas',   label: 'Thomas',   voiceId: 'GBv7mTt0atIp3Br8iCZE', gender: 'M', desc: 'Dark & brooding' },
  { id: 'george',   label: 'George',   voiceId: 'JBFqnCBsd6RMkjVDRZzb', gender: 'M', desc: 'Rich & unhurried' },
  { id: 'antoni',   label: 'Antoni',   voiceId: 'ErXwobaYiN019PkySvjV', gender: 'M', desc: 'Smooth & seductive' },
  { id: 'adam',     label: 'Adam',     voiceId: 'pNInz6obpgDQGcFmaJgB', gender: 'M', desc: 'Deep & commanding' },
  { id: 'aria',     label: 'Aria',     voiceId: '9BWtsMINqrJLrRacOk9x', gender: 'F', desc: 'Warm & hypnotic' },
  { id: 'sarah',    label: 'Sarah',    voiceId: 'EXAVITQu4vr4xnSDxMaL', gender: 'F', desc: 'Soft & breathy' },
  { id: 'laura',    label: 'Laura',    voiceId: 'FGY2WhTYpPnrIDTdsKH5', gender: 'F', desc: 'Clear & intimate' },
  { id: 'charlotte',label: 'Charlotte',voiceId: 'XB0fDUnXU5powFXDhCwa', gender: 'F', desc: 'British & literary' },
  { id: 'matilda',  label: 'Matilda',  voiceId: 'XrExE9yKIg1WjnnlVkGX', gender: 'F', desc: 'Playful & charged' },
];

const MOODS = [
  { id: 'longing',   label: 'Longing',   em: '🌙' },
  { id: 'electric',  label: 'Electric',  em: '⚡' },
  { id: 'tender',    label: 'Tender',    em: '🌸' },
  { id: 'forbidden', label: 'Forbidden', em: '🥀' },
  { id: 'nostalgic', label: 'Nostalgic', em: '🕯️' },
  { id: 'wild',      label: 'Wild',      em: '🌊' },
];

const SETTINGS = [
  { id: 'paris',    label: 'Paris, 2am' },
  { id: 'hotel',    label: 'Strangers in a Hotel' },
  { id: 'rain',     label: 'Rainy City Night' },
  { id: 'cabin',    label: 'Secluded Cabin' },
  { id: 'office',   label: 'After Hours at the Office' },
  { id: 'beach',    label: 'Deserted Beach' },
];

const THEMES = [
  { id: 'reunion',   label: 'Reunion' },
  { id: 'strangers', label: 'Strangers' },
  { id: 'forbidden', label: 'Forbidden' },
  { id: 'tension',   label: 'Slow Burn' },
  { id: 'power',     label: 'Power Play' },
  { id: 'tenderness',label: 'Pure Tenderness' },
];

const INTENSITY_LABELS = ['Tender', 'Flirty', 'Sensual', 'Steamy', 'Explicit'];

const MOOD_PHOTOS = {
  longing:   '🌙',
  electric:  '⚡',
  tender:    '🌸',
  forbidden: '🥀',
  nostalgic: '🕯️',
  wild:      '🌊',
};
const MOOD_GRADIENTS = {
  longing:   'linear-gradient(135deg, #1a1035 0%, #2d1f5e 50%, #0d0820 100%)',
  electric:  'linear-gradient(135deg, #0a1628 0%, #1a3a5c 50%, #0d1f3c 100%)',
  tender:    'linear-gradient(135deg, #2d1228 0%, #5c1e4a 50%, #1a0d1a 100%)',
  forbidden: 'linear-gradient(135deg, #1a0a0a 0%, #3d1515 50%, #0d0505 100%)',
  nostalgic: 'linear-gradient(135deg, #1a1505 0%, #3d3010 50%, #0d0c05 100%)',
  wild:      'linear-gradient(135deg, #051a2d 0%, #0d3d5c 50%, #020d1a 100%)',
};

function autoTitle(config) {
  const moodLabel = MOODS.find(m => m.id === config.mood)?.label || config.customMood || '';
  const settingLabel = SETTINGS.find(s => s.id === config.setting)?.label || config.customSetting || '';
  const names = config.yourName && config.loverName
    ? `${config.yourName} & ${config.loverName}`
    : config.yourName || '';
  const parts = [moodLabel, settingLabel, names].filter(Boolean);
  if (parts.length === 0) return 'Your Story';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} · ${parts[1]}${names ? ' · ' + names : ''}`;
}

const C = {
  black:    '#09080c',
  deep:     '#110f16',
  card:     '#15121e',
  raised:   '#1d1929',
  violet:   '#5a3d99',
  accent:   '#7c5cbf',
  lilac:    '#c9a0e8',
  ivory:    '#f2ece0',
  muted:    'rgba(242,236,224,0.38)',
  faint:    'rgba(242,236,224,0.08)',
  border:   'rgba(242,236,224,0.07)',
  gold:     '#e2b96a',
};

const font = {
  display: "'DM Serif Display', serif",
  body:    "'Playfair Display', serif",
  ui:      "'Inter', sans-serif",
};

export default function Studio() {
  const navigate = useNavigate();
  const [view, setView] = useState('library'); // library | create | generating | player
  const [stories, setStories] = useState([]);

  // Load stories from backend on mount
  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('../supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/stories`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data.stories || []);
          if (list.length > 0) {
            // Normalise DB fields to match in-memory story shape
            const normalised = list.map(s => ({
              ...s,
              audioUrl:   s.audioUrl   || s.audio_url,
              mood:       s.mood       || s.config?.mood,
              narrator:   s.narrator   || s.config?.narrator,
              yourName:   s.yourName   || s.config?.yourName  || '',
              loverName:  s.loverName  || s.config?.loverName || '',
            }));
            setStories(normalised);
          }
        }
      } catch (e) {
        console.warn('Could not load stories:', e.message);
      }
    })();
  }, []);
  const [currentStory, setCurrentStory] = useState(null);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const [storyTitle, setStoryTitle] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [config, setConfig] = useState({
    mood: '', customMood: '',
    setting: '', customSetting: '',
    themes: [], customTheme: '',
    narrator: '',
    intensity: 3,
    pov: 'second',
    yourName: '', loverName: '',
    yourGender: '', loverGender: '',
  });

  // ── Helpers ──────────────────────────────────────────────────────────────
  const toggleTheme = (id) =>
    setConfig(c => ({
      ...c,
      themes: c.themes.includes(id) ? c.themes.filter(t => t !== id) : [...c.themes, id],
    }));

  const previewVoice = (voiceId) => {
    if (window._previewLoading) return;
    if (window._previewAudio) { window._previewAudio.pause(); window._previewAudio = null; }
    window._previewLoading = true;
    fetch(`${process.env.REACT_APP_API_URL}/api/generate/preview/${voiceId}?t=${Date.now()}`)
      .then(r => r.blob())
      .then(b => {
        const a = new Audio(URL.createObjectURL(b));
        window._previewAudio = a;
        window._previewLoading = false;
        a.play();
        a.onended = () => { window._previewAudio = null; };
      })
      .catch(() => { window._previewLoading = false; });
  };

  const canGenerate =
    (config.mood || config.customMood) &&
    (config.setting || config.customSetting) &&
    config.narrator;

  const generateStory = async () => {
    if (!canGenerate) return;
    setError('');
    setView('generating');
    try {
      const token = (await import('../supabase')).supabase.auth.getSession
        ? (await (await import('../supabase')).supabase.auth.getSession()).data.session?.access_token
        : null;

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          mood:         config.mood,
          customMood:   config.customMood,
          setting:      config.setting,
          customSetting: config.customSetting,
          themes:       config.themes,
          customTheme:  config.customTheme,
          narrator:     config.narrator,
          intensity:    config.intensity,
          pov:          config.pov,
          yourName:     config.yourName,
          loverName:    config.loverName,
          yourGender:   config.yourGender,
          loverGender:  config.loverGender,
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || 'Generation failed');
      }
      const data = await res.json();
      const moodKey = config.mood || config.customMood || '';
      const generatedTitle = data.title && data.title !== 'Untitled' ? data.title : autoTitle(config);
      const photo = MOOD_PHOTOS[config.mood] || '🌙';
      const gradient = MOOD_GRADIENTS[config.mood] || MOOD_GRADIENTS.longing;
      const story = {
        id:        data.storyId || Date.now(),
        title:     generatedTitle,
        audioUrl:  data.audioUrl,
        mood:      moodKey,
        setting:   config.setting || config.customSetting,
        narrator:  config.narrator,
        yourName:  config.yourName,
        loverName: config.loverName,
        photo,
        gradient,
        createdAt: new Date().toISOString(),
      };
      setStoryTitle(generatedTitle);
      setEditingTitle(false);
      setStories(prev => [story, ...prev]);
      setCurrentStory(story);
      setView('player');
    } catch (e) {
      setError(e.message);
      setView('create');
    }
  };

  // ── Audio controls ────────────────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoad = () => setDuration(audio.duration);
    const onEnd  = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoad);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoad);
      audio.removeEventListener('ended', onEnd);
    };
  }, [currentStory]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); setIsPlaying(false); }
    else           { audio.play();  setIsPlaying(true);  }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * duration;
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // ── Shared style atoms ────────────────────────────────────────────────────
  const s = {
    page: {
      minHeight: '100vh',
      background: C.black,
      color: C.ivory,
      fontFamily: C.ui,
      fontWeight: 300,
    },
    label: {
      fontSize: '9px',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      color: C.accent,
      opacity: 0.9,
      marginBottom: '12px',
      fontFamily: font.ui,
    },
    sectionWrap: { marginBottom: '28px' },
    chipRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    chip: (active) => ({
      padding: '9px 16px',
      borderRadius: '24px',
      border: `1px solid ${active ? 'rgba(124,92,191,0.55)' : C.border}`,
      background: active ? 'rgba(90,61,153,0.18)' : 'rgba(255,255,255,0.02)',
      color: active ? C.lilac : C.muted,
      fontSize: '13px',
      fontFamily: font.body,
      fontStyle: 'italic',
      cursor: 'pointer',
      transition: 'all 0.15s',
    }),
    moodTile: (active) => ({
      padding: '13px 6px',
      borderRadius: '14px',
      border: `1px solid ${active ? 'rgba(124,92,191,0.5)' : C.border}`,
      background: active ? 'rgba(90,61,153,0.14)' : 'rgba(255,255,255,0.02)',
      textAlign: 'center',
      cursor: 'pointer',
    }),
    input: (filled) => ({
      width: '100%',
      padding: '13px 16px',
      background: C.raised,
      border: `1px solid ${filled ? 'rgba(124,92,191,0.45)' : 'rgba(124,92,191,0.18)'}`,
      borderRadius: '14px',
      color: C.ivory,
      fontSize: '14px',
      fontFamily: font.body,
      fontStyle: 'italic',
      outline: 'none',
      boxSizing: 'border-box',
    }),
    textarea: {
      width: '100%',
      padding: '14px 16px',
      background: C.raised,
      border: '1px solid rgba(124,92,191,0.25)',
      borderRadius: '16px',
      color: C.ivory,
      fontSize: '14px',
      fontFamily: font.body,
      fontStyle: 'italic',
      outline: 'none',
      resize: 'none',
      lineHeight: 1.6,
      boxSizing: 'border-box',
    },
    btnPrimary: (enabled) => ({
      width: '100%',
      padding: '17px',
      background: enabled
        ? 'linear-gradient(135deg, #5a3d99, #7c5cbf)'
        : 'rgba(90,61,153,0.15)',
      border: 'none',
      borderRadius: '14px',
      color: enabled ? C.ivory : 'rgba(242,236,224,0.25)',
      fontFamily: font.display,
      fontStyle: 'italic',
      fontSize: '20px',
      cursor: enabled ? 'pointer' : 'not-allowed',
      boxShadow: enabled ? '0 8px 28px rgba(90,61,153,0.3)' : 'none',
      transition: 'all 0.2s',
    }),
    narRow: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 14px',
      borderRadius: '14px',
      border: `1px solid ${active ? 'rgba(124,92,191,0.45)' : C.border}`,
      background: active ? 'rgba(90,61,153,0.1)' : 'rgba(255,255,255,0.02)',
      cursor: 'pointer',
      marginBottom: '8px',
    }),
    gBtn: (active) => ({
      flex: 1,
      padding: '7px 4px',
      borderRadius: '8px',
      border: `1px solid ${active ? 'rgba(124,92,191,0.5)' : C.border}`,
      background: active ? 'rgba(90,61,153,0.2)' : 'rgba(255,255,255,0.02)',
      color: active ? C.lilac : C.muted,
      fontSize: '11px',
      cursor: 'pointer',
      fontFamily: font.ui,
    }),
  };

  // ── Tab bar ───────────────────────────────────────────────────────────────
  const TabBar = ({ active }) => (
    <div style={{
      display: 'flex',
      background: 'rgba(9,8,12,0.98)',
      borderTop: `1px solid ${C.border}`,
      padding: '12px 0 8px',
      position: 'sticky',
      bottom: 0,
    }}>
      {[
        { id: 'library', icon: '📚', label: 'Library' },
        { id: 'create',  icon: '✨', label: 'Create'  },
        { id: 'account', icon: '👤', label: 'Account' },
      ].map(t => (
        <div
          key={t.id}
          onClick={() => t.id === 'account' ? navigate('/account') : setView(t.id)}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
        >
          <div style={{ fontSize: '20px', opacity: active === t.id ? 1 : 0.2 }}>{t.icon}</div>
          <div style={{
            fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase',
            color: active === t.id ? C.accent : 'rgba(242,236,224,0.2)',
          }}>{t.label}</div>
        </div>
      ))}
    </div>
  );

  // ── LIBRARY ───────────────────────────────────────────────────────────────
  if (view === 'library') return (
    <div style={s.page}>
      <div style={{ padding: '24px 22px 0' }}>
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '9px', letterSpacing: '3px', textTransform: 'uppercase', color: 'rgba(242,236,224,0.2)', marginBottom: '4px', fontFamily: font.ui }}>
            Good evening
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '36px' }}>Your Stories</div>
            <div
              onClick={() => setView('create')}
              style={{ fontSize: '11px', color: C.accent, border: `1px solid rgba(124,92,191,0.3)`, padding: '7px 16px', borderRadius: '20px', cursor: 'pointer', letterSpacing: '1px', fontFamily: font.ui }}
            >
              + New
            </div>
          </div>
        </div>

        {stories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px', opacity: 0.4 }}>🎙</div>
            <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '20px', marginBottom: '8px', color: C.muted }}>
              No stories yet
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(242,236,224,0.25)', marginBottom: '28px', lineHeight: 1.6 }}>
              Create your first story — tell us exactly what you want.
            </div>
            <button onClick={() => setView('create')} style={s.btnPrimary(true)}>
              Create your first story
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
            {stories.map(story => (
              <div
                key={story.id}
                onClick={() => { setCurrentStory({ ...story, audioUrl: story.audioUrl || story.audio_url }); setView('player'); }}
                style={{ display: 'flex', gap: '12px', padding: '14px', borderRadius: '16px', border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.015)', cursor: 'pointer', alignItems: 'center' }}
              >
                <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: story.gradient || 'linear-gradient(135deg, rgba(90,61,153,0.35), rgba(17,15,22,0.9))', border: `1px solid rgba(124,92,191,0.15)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                  {story.photo || MOOD_PHOTOS[story.mood] || '🌙'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: font.body, fontSize: '14px', fontStyle: 'italic', marginBottom: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {editingTitle ? (
              <input
                autoFocus
                value={storyTitle}
                onChange={e => setStoryTitle(e.target.value)}
                onBlur={() => {
                  setEditingTitle(false);
                  setCurrentStory(s => ({ ...s, title: storyTitle }));
                  setStories(prev => prev.map(s => s.id === currentStory.id ? { ...s, title: storyTitle } : s));
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') e.target.blur();
                  if (e.key === 'Escape') { setStoryTitle(currentStory.title); setEditingTitle(false); }
                }}
                style={{ background: 'transparent', border: 'none', borderBottom: '1px solid rgba(124,92,191,0.5)', color: '#f2ece0', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '18px', width: '100%', outline: 'none', padding: '2px 0', textAlign: 'center' }}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }} onClick={() => { setStoryTitle(currentStory?.title || ''); setEditingTitle(true); }}>
                <span>{storyTitle || currentStory?.title || 'Your Story'}</span>
                <span style={{ fontSize: '11px', color: 'rgba(124,92,191,0.6)' }}>✏️</span>
              </div>
            )}
                  </div>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    {story.yourName && story.loverName && (
                      <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '10px', background: 'rgba(90,61,153,0.2)', border: `1px solid rgba(124,92,191,0.35)`, color: C.lilac }}>
                        {story.yourName} & {story.loverName}
                      </span>
                    )}
                    <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '10px', background: 'rgba(124,92,191,0.08)', border: `1px solid rgba(124,92,191,0.12)`, color: 'rgba(242,236,224,0.35)' }}>
                      {story.mood}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <TabBar active="library" />
    </div>
  );

  // ── CREATE ────────────────────────────────────────────────────────────────
  if (view === 'create') return (
    <div style={s.page}>
      <div style={{ padding: '22px 22px 120px' }}>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '32px', marginBottom: '6px' }}>
            Your Story
          </div>
          <div style={{ fontSize: '13px', color: C.muted, lineHeight: 1.6 }}>
            Every detail shapes something made only for you.
          </div>
        </div>

        {/* ── Mood ── */}
        <div style={s.sectionWrap}>
          <div style={s.label}>Mood</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '7px', marginBottom: '10px' }}>
            {MOODS.map(m => (
              <div
                key={m.id}
                onClick={() => setConfig(c => ({ ...c, mood: c.mood === m.id ? '' : m.id, customMood: '' }))}
                style={s.moodTile(config.mood === m.id)}
              >
                <span style={{ fontSize: '18px', display: 'block', marginBottom: '4px' }}>{m.em}</span>
                <span style={{ fontSize: '10px', color: config.mood === m.id ? C.lilac : C.muted }}>{m.label}</span>
              </div>
            ))}
          </div>
          <input
            value={config.customMood}
            onChange={e => setConfig(c => ({ ...c, customMood: e.target.value, mood: '' }))}
            placeholder="Or describe your own mood…"
            style={s.input(!!config.customMood)}
          />
        </div>

        {/* ── Setting ── */}
        <div style={s.sectionWrap}>
          <div style={s.label}>Setting</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '10px' }}>
            {SETTINGS.map(setting => (
              <div
                key={setting.id}
                onClick={() => setConfig(c => ({ ...c, setting: c.setting === setting.id ? '' : setting.id, customSetting: '' }))}
                style={{
                  padding: '12px 16px',
                  borderRadius: '13px',
                  border: `1px solid ${config.setting === setting.id ? 'rgba(124,92,191,0.5)' : C.border}`,
                  background: config.setting === setting.id ? 'rgba(90,61,153,0.12)' : 'rgba(255,255,255,0.02)',
                  fontFamily: font.body,
                  fontStyle: 'italic',
                  fontSize: '14px',
                  color: config.setting === setting.id ? C.ivory : C.muted,
                  cursor: 'pointer',
                }}
              >
                {setting.label}
              </div>
            ))}
          </div>
          <input
            value={config.customSetting}
            onChange={e => setConfig(c => ({ ...c, customSetting: e.target.value, setting: '' }))}
            placeholder="Or describe your own setting…"
            style={s.input(!!config.customSetting)}
          />
        </div>

        {/* ── Themes ── */}
        <div style={s.sectionWrap}>
          <div style={s.label}>Themes <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: '11px', opacity: 0.5 }}>(pick any)</span></div>
          <div style={{ ...s.chipRow, marginBottom: '10px' }}>
            {THEMES.map(t => (
              <div key={t.id} onClick={() => toggleTheme(t.id)} style={s.chip(config.themes.includes(t.id))}>
                {t.label}
              </div>
            ))}
          </div>
          <input
            value={config.customTheme}
            onChange={e => setConfig(c => ({ ...c, customTheme: e.target.value }))}
            placeholder="Or add your own theme…"
            style={s.input(!!config.customTheme)}
          />
        </div>

        {/* ── Tell us anything ── */}
        <div style={{ ...s.sectionWrap, background: C.card, border: `1px solid rgba(124,92,191,0.2)`, borderRadius: '18px', padding: '18px', boxShadow: '0 0 24px rgba(90,61,153,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <span style={{ fontSize: '14px' }}>✦</span>
            <div style={{ ...s.label, marginBottom: 0 }}>Tell us anything else</div>
          </div>
          <div style={{ fontSize: '12px', color: C.muted, fontFamily: font.body, fontStyle: 'italic', marginBottom: '12px', lineHeight: 1.6 }}>
            Describe a fantasy, a specific detail, a dynamic — anything you want in the story.
          </div>
          <textarea
            rows={4}
            value={config.customTheme}
            onChange={e => setConfig(c => ({ ...c, customTheme: e.target.value }))}
            placeholder="e.g. We've been pretending nothing's happening for months. He finally says my name and I can't pretend anymore…"
            style={s.textarea}
          />
        </div>

        {/* ── Characters ── */}
        <div style={{ ...s.sectionWrap, background: C.card, border: `1px solid rgba(124,92,191,0.22)`, borderRadius: '18px', padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{ fontSize: '14px' }}>✦</span>
            <div style={{ ...s.label, marginBottom: 0 }}>Make it yours</div>
          </div>
          <div style={{ fontSize: '12px', color: C.muted, fontFamily: font.body, fontStyle: 'italic', marginBottom: '14px', lineHeight: 1.5 }}>
            Add your characters' names and the story is written around them.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-7px', left: '12px', fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: C.accent, background: C.card, padding: '0 4px' }}>You</div>
              <input value={config.yourName} onChange={e => setConfig(c => ({ ...c, yourName: e.target.value }))} placeholder="Your name…" style={s.input(!!config.yourName)} />
            </div>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-7px', left: '12px', fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: C.accent, background: C.card, padding: '0 4px' }}>Them</div>
              <input value={config.loverName} onChange={e => setConfig(c => ({ ...c, loverName: e.target.value }))} placeholder="Their name…" style={s.input(!!config.loverName)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(242,236,224,0.2)', marginBottom: '6px', fontFamily: font.ui }}>Your gender</div>
              <div style={{ display: 'flex', gap: '5px' }}>
                {['Female', 'Male', 'Neutral'].map(g => (
                  <button key={g} onClick={() => setConfig(c => ({ ...c, yourGender: c.yourGender === g ? '' : g }))} style={s.gBtn(config.yourGender === g)}>{g[0]}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(242,236,224,0.2)', marginBottom: '6px', fontFamily: font.ui }}>Their gender</div>
              <div style={{ display: 'flex', gap: '5px' }}>
                {['Female', 'Male', 'Neutral'].map(g => (
                  <button key={g} onClick={() => setConfig(c => ({ ...c, loverGender: c.loverGender === g ? '' : g }))} style={s.gBtn(config.loverGender === g)}>{g[0]}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Intensity ── */}
        <div style={s.sectionWrap}>
          <div style={s.label}>Intensity — <span style={{ color: C.lilac, textTransform: 'none', letterSpacing: 0, fontSize: '12px' }}>{INTENSITY_LABELS[config.intensity - 1]}</span></div>
          <input
            type="range" min={1} max={5} value={config.intensity}
            onChange={e => setConfig(c => ({ ...c, intensity: Number(e.target.value) }))}
            style={{ width: '100%', accentColor: C.accent, marginBottom: '6px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'rgba(242,236,224,0.2)' }}>
            <span>Tender</span><span>Explicit</span>
          </div>
        </div>

        {/* ── Narrator ── */}
        <div style={s.sectionWrap}>
          <div style={s.label}>Narrator</div>
          <div style={{ fontSize: '11px', color: 'rgba(242,236,224,0.25)', marginBottom: '10px', letterSpacing: '1px' }}>Female</div>
          {NARRATORS.filter(n => n.gender === 'F').map(n => (
            <div key={n.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <div onClick={() => setConfig(c => ({ ...c, narrator: n.id }))} style={s.narRow(config.narrator === n.id)}>
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(90,61,153,0.4), rgba(17,15,22,0.9))', border: `1px solid rgba(124,92,191,0.2)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>👩</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', marginBottom: '1px' }}>{n.label}</div>
                  <div style={{ fontSize: '11px', color: C.muted, fontFamily: font.body, fontStyle: 'italic' }}>{n.desc}</div>
                </div>
              </div>
              <button
                onClick={() => previewVoice(n.voiceId)}
                style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(124,92,191,0.1)', border: `1px solid rgba(124,92,191,0.25)`, color: C.accent, cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}
              >▶</button>
            </div>
          ))}
          <div style={{ fontSize: '11px', color: 'rgba(242,236,224,0.25)', margin: '10px 0 10px', letterSpacing: '1px' }}>Male</div>
          {NARRATORS.filter(n => n.gender === 'M').map(n => (
            <div key={n.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <div onClick={() => setConfig(c => ({ ...c, narrator: n.id }))} style={s.narRow(config.narrator === n.id)}>
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(90,61,153,0.4), rgba(17,15,22,0.9))', border: `1px solid rgba(124,92,191,0.2)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>👨</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', marginBottom: '1px' }}>{n.label}</div>
                  <div style={{ fontSize: '11px', color: C.muted, fontFamily: font.body, fontStyle: 'italic' }}>{n.desc}</div>
                </div>
              </div>
              <button
                onClick={() => previewVoice(n.voiceId)}
                style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(124,92,191,0.1)', border: `1px solid rgba(124,92,191,0.25)`, color: C.accent, cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}
              >▶</button>
            </div>
          ))}
        </div>

        {error && (
          <div style={{ color: '#e88a8a', fontSize: '13px', marginBottom: '16px', textAlign: 'center', padding: '10px', background: 'rgba(232,138,138,0.08)', borderRadius: '10px', border: '1px solid rgba(232,138,138,0.15)' }}>
            {error}
          </div>
        )}

        <button onClick={generateStory} disabled={!canGenerate} style={s.btnPrimary(canGenerate)}>
          Write My Story
        </button>
        <button onClick={() => setView('library')} style={{ width: '100%', marginTop: '12px', padding: '10px', background: 'none', border: 'none', color: 'rgba(242,236,224,0.25)', cursor: 'pointer', fontSize: '13px', fontFamily: font.ui }}>
          ← Back
        </button>
      </div>
      <TabBar active="create" />
    </div>
  );

  // ── GENERATING ────────────────────────────────────────────────────────────
  if (view === 'generating') return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 24px' }}>
      <div>
        <div style={{ fontSize: '52px', marginBottom: '28px', animation: 'pulse 2s ease-in-out infinite' }}>✦</div>
        <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '26px', marginBottom: '10px', color: C.ivory }}>
          Writing your story…
        </div>
        <div style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7 }}>
          {config.yourName ? `Crafting every word for ${config.yourName}` : 'Crafting every word for you'}
        </div>
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.9)} }`}</style>
    </div>
  );

  // ── PLAYER ────────────────────────────────────────────────────────────────
  if (view === 'player' && currentStory) {
    const progress = duration ? (currentTime / duration) * 100 : 0;
    return (
      <div style={{ ...s.page, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse 80% 40% at 50% 10%, rgba(90,61,153,0.2) 0%, transparent 60%), ${C.black}`, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, padding: '22px 22px 36px', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div onClick={() => setView('library')} style={{ fontSize: '11px', color: C.muted, cursor: 'pointer', letterSpacing: '1px' }}>← Library</div>
            <div style={{ fontSize: '17px', color: C.muted }}>···</div>
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '24px', padding: '24px 22px 20px', marginBottom: '20px', flex: 1 }}>
            <div style={{ fontSize: '9px', letterSpacing: '4px', textTransform: 'uppercase', color: C.accent, opacity: 0.6, marginBottom: '10px', fontFamily: font.ui }}>Now Playing</div>
            <div style={{ fontFamily: font.body, fontSize: '26px', fontStyle: 'italic', lineHeight: 1.3, marginBottom: '6px' }}>
              {editingTitle ? (
                <input
                  autoFocus
                  value={storyTitle}
                  onChange={e => setStoryTitle(e.target.value)}
                  onBlur={() => {
                    setEditingTitle(false);
                    setCurrentStory(s => ({ ...s, title: storyTitle }));
                    setStories(prev => prev.map(s => s.id === currentStory.id ? { ...s, title: storyTitle } : s));
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') e.target.blur();
                    if (e.key === 'Escape') { setStoryTitle(currentStory.title); setEditingTitle(false); }
                  }}
                  style={{ background: 'transparent', border: 'none', borderBottom: '1px solid rgba(124,92,191,0.5)', color: '#f2ece0', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '24px', width: '100%', outline: 'none', padding: '2px 0', textAlign: 'center' }}
                />
              ) : (
                <div
                  onClick={() => { setStoryTitle(currentStory?.title || ''); setEditingTitle(true); }}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  <span>{storyTitle || currentStory?.title || 'Your Story'}</span>
                  <span style={{ fontSize: '12px', opacity: 0.4 }}>✏️</span>
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1px', marginBottom: '14px', fontFamily: font.ui }}>
              {NARRATORS.find(n => n.id === currentStory.narrator)?.label} · {currentStory.mood}
            </div>

            {(currentStory.yourName || currentStory.loverName) && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: 'rgba(90,61,153,0.1)', border: `1px solid rgba(124,92,191,0.2)`, borderRadius: '10px', padding: '10px 12px', marginBottom: '18px' }}>
                <span style={{ fontSize: '12px', flexShrink: 0, marginTop: '1px' }}>✦</span>
                <div style={{ fontSize: '11px', color: C.lilac, lineHeight: 1.6, fontFamily: font.body, fontStyle: 'italic' }}>
                  Written for <strong>{currentStory.yourName}</strong>{currentStory.loverName ? ` & ${currentStory.loverName}` : ''} — this story exists nowhere else.
                </div>
              </div>
            )}

            {/* Waveform / scrub bar */}
            <div onClick={seek} style={{ height: '44px', display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '10px', cursor: 'pointer' }}>
              {Array.from({ length: 30 }).map((_, i) => {
                const h = 8 + Math.sin(i * 0.8) * 8 + Math.cos(i * 0.3) * 6 + Math.random() * 4;
                const pct = (i / 30) * 100;
                return (
                  <div key={i} style={{
                    flex: 1, borderRadius: '2px',
                    height: `${h}px`,
                    background: pct < progress
                      ? (pct < progress - 3 ? 'rgba(124,92,191,0.6)' : C.lilac)
                      : C.faint,
                    boxShadow: pct >= progress - 3 && pct < progress ? `0 0 6px rgba(201,160,232,0.4)` : 'none',
                  }} />
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'rgba(242,236,224,0.2)' }}>
              <span>{fmt(currentTime)}</span><span>{fmt(duration)}</span>
            </div>
          </div>

          {currentStory.audioUrl && (
            <audio ref={audioRef} src={currentStory.audioUrl} />
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '28px', marginBottom: '20px' }}>
            <button style={{ background: 'none', border: 'none', color: 'rgba(242,236,224,0.25)', fontSize: '18px', cursor: 'pointer' }}>⏮</button>
            <button onClick={() => { if (audioRef.current) { audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10); } }} style={{ background: 'none', border: 'none', color: 'rgba(242,236,224,0.3)', fontSize: '20px', cursor: 'pointer' }}>«</button>
            <button
              onClick={togglePlay}
              style={{ width: '62px', height: '62px', borderRadius: '50%', background: 'linear-gradient(135deg, #5a3d99, #7c5cbf)', border: 'none', color: C.ivory, fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(90,61,153,0.4)', cursor: 'pointer' }}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button onClick={() => { if (audioRef.current) { audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10); } }} style={{ background: 'none', border: 'none', color: 'rgba(242,236,224,0.3)', fontSize: '20px', cursor: 'pointer' }}>»</button>
            <button style={{ background: 'none', border: 'none', color: 'rgba(242,236,224,0.25)', fontSize: '18px', cursor: 'pointer' }}>⏭</button>
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '18px', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '19px', marginBottom: '8px' }}>Want another?</div>
            <div style={{ fontSize: '11px', color: C.muted, lineHeight: 1.7, marginBottom: '16px' }}>
              Create a new story with different characters, moods, anything you want.
            </div>
            <button onClick={() => { setConfig({ mood: '', customMood: '', setting: '', customSetting: '', themes: [], customTheme: '', narrator: '', intensity: 3, pov: 'second', yourName: '', loverName: '', yourGender: '', loverGender: '' }); setView('create'); }} style={s.btnPrimary(true)}>
              Create another
            </button>
          </div>
        </div>
        <TabBar active="library" />
      </div>
    );
  }

  return null;
}
